import React from 'react';
import { Box, Typography } from '@mui/material';
import CertificateHeader from './CertificateHeader';


/**
 * School Leaving Certificate - Landscape Format
 * Matches the actual printed LC from Vidya Pratishthan's school
 * Landscape orientation with school header on left, 16 rows
 */
export const LCTemplateLandscape = ({ data }) => {
    const { school, student, details, issueDate } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        } catch (e) {
            return dateStr;
        }
    };

    const rows = [
        { num: 1, label: 'Name of Student', value: student?.name || '', sub: [
            { label: 'Father Name:', value: student?.fatherName || '' },
            { label: 'Mother Name:', value: student?.motherName || '' },
        ]},
        { num: 2, label: 'Nationality :', value: details?.nationality || 'Indian' },
        { num: 3, label: 'Mother Tongue :', value: details?.motherTongue || 'Marathi' },
        { num: 4, label: 'Religion :', value: details?.religion || '' },
        { num: 5, label: 'Cast :', value: details?.caste || '' },
        { num: 6, label: 'Sub Cast :', value: details?.subCaste || '' },
        { num: 7, label: 'Place of Birth :', value: details?.placeOfBirth || '', sub: [
            { label: 'Taluka:', value: details?.taluka || '' },
            { label: 'District:', value: details?.district || 'Pune' },
            { label: 'State:', value: details?.state || 'Maharashtra' },
            { label: 'Country:', value: details?.country || 'India' },
        ]},
        { num: 8, label: 'Date of Birth a)Figure (According to Christian)', value: formatDate(details?.dateOfBirth), sub: [
            { label: 'b) In word', value: details?.dateOfBirthInWords || '' },
        ]},
        { num: 9, label: 'Last School Attended And Standard', value: `${details?.lastSchool || ''} ${details?.lastSchoolClass ? 'Std. ' + details.lastSchoolClass : ''}` },
        { num: 10, label: 'Date of Admission With Class :', value: `${formatDate(details?.dateOfAdmission)} ${details?.classAtAdmission ? 'Std. ' + details.classAtAdmission : ''}` },
        { num: 11, label: 'Progress :', value: details?.progress || 'Good' },
        { num: 12, label: 'Conduct :', value: details?.conduct || 'Good' },
        { num: 13, label: 'Date of Leaving School :', value: formatDate(details?.dateOfLeaving) },
        { num: 14, label: 'Std. In which Studying and Since When', value: `Std. ${details?.currentClass || ''} ${details?.studyingSince || ''}` },
        { num: 15, label: 'Reason of Leaving School:', value: details?.reasonForLeaving || '' },
        { num: 16, label: 'Remark :', value: details?.remarks || '' },
    ];

    return (
        <Box sx={{
            fontFamily: '"Times New Roman", Georgia, serif',
            fontSize: '11px',
            lineHeight: 1.3,
            color: '#000',
            backgroundColor: '#fff',
            p: '8mm',
            width: '297mm',
            minHeight: '210mm',
            boxSizing: 'border-box',
            '@media print': {
                width: '297mm',
                margin: 0,
                padding: '8mm',
                '@page': { size: 'landscape' }
            }
        }}>
            {/* Unified School Header */}
            <CertificateHeader isLeavingCertificate={true} details={details} school={school} />

            {/* Right - Numbers (moved below header for clean alignment if landscape) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: 11 }}>
                <Typography sx={{ fontSize: 11 }}>
                    SR.NO:- <strong>{details?.srNumber || '____'}</strong>
                </Typography>
                <Typography sx={{ fontSize: 11 }}>
                    General Register NO:- <strong>{details?.generalRegisterNo || student?.admissionNumber || '____'}</strong>
                </Typography>
            </Box>


            {/* UDISE Row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontSize: 10 }}>
                <Typography sx={{ fontSize: 10 }}>
                    School U-DISE Number – <strong>{school?.udiseNumber || '27250200605'}</strong>
                </Typography>
            </Box>

            {/* Certificate Title */}
            <Typography sx={{
                textAlign: 'center', fontSize: 16, fontWeight: 'bold',
                textDecoration: 'underline', mb: 1, fontStyle: 'italic'
            }}>
                School Leaving Certificate
            </Typography>

            {/* Student ID Row */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', mb: 1,
                p: 0.5, border: '1px solid #000', fontSize: 10
            }}>
                <span>Student ID Number: <strong>{student?.admissionNumber}</strong></span>
                <span>Student UID (Aadhar Card) : <strong>{details?.uid || '_____________'}</strong></span>
                <span>Student PEN No : <strong>{details?.penNumber || '_____________'}</strong></span>
            </Box>

            {/* Main Table */}
            <Box sx={{ border: '1px solid #000' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        {rows.map((row) => (
                            <React.Fragment key={row.num}>
                                <tr style={{ borderBottom: '1px solid #ccc' }}>
                                    <td style={{
                                        width: 30, padding: '3px 6px', borderRight: '1px solid #ccc',
                                        textAlign: 'center', fontSize: 10, fontWeight: 600
                                    }}>
                                        {row.num}
                                    </td>
                                    <td style={{
                                        width: '38%', padding: '3px 8px', borderRight: '1px solid #ccc',
                                        fontSize: 11
                                    }}>
                                        {row.label}
                                    </td>
                                    <td style={{
                                        padding: '3px 8px', fontSize: 11, fontWeight: 'bold'
                                    }}>
                                        {row.value || '-'}
                                    </td>
                                </tr>
                                {/* Sub-rows */}
                                {row.sub && row.sub.map((sub, si) => (
                                    <tr key={`${row.num}-${si}`} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '2px 6px', borderRight: '1px solid #ccc' }}></td>
                                        <td style={{
                                            padding: '2px 8px 2px 24px', borderRight: '1px solid #ccc',
                                            fontSize: 10
                                        }}>
                                            {sub.label}
                                        </td>
                                        <td style={{ padding: '2px 8px', fontSize: 10, fontWeight: 'bold' }}>
                                            {sub.value || '-'}
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </Box>

            {/* Disclaimer */}
            <Typography sx={{ fontSize: 10, mt: 1 }}>
                This above information is in accordance with the school Register.
            </Typography>

            {/* Signature Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography sx={{ fontSize: 11 }}>Date: {issueDate ? formatDate(issueDate) : formatDate(new Date())}</Typography>
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ borderTop: '1px solid #000', width: 100, mb: 0.3, mt: 3 }} />
                    <Typography sx={{ fontSize: 11 }}>Clerk</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ borderTop: '1px solid #000', width: 100, mb: 0.3, mt: 3 }} />
                    <Typography sx={{ fontSize: 11 }}>Principal</Typography>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #000', fontSize: 9 }}>
                <Typography sx={{ fontSize: 9 }}>
                    No change in any entry is to made except by authority issuing the
                    Leaving Certificate. Any infringement will be punished with rustication.
                </Typography>
            </Box>
        </Box>
    );
};

export default LCTemplateLandscape;
